import { useReportClock } from '../reportClock';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import Chart from 'react-apexcharts';
import { AcivityChart } from '../../../Data/ApexChart';
import { loadDashboardActivityData, WEEK_LABELS } from './activityData';
import i18n from '../../../i18n';
import AvisDetailModal, { AvisDetailModalTrigger } from '../Avis/AvisDetailModal';

const emptyActivityState = {
    activities: [],
    currentWeekCounts: Array(7).fill(0),
    currentWeekTotal: 0,
    previousWeekTotal: 0,
    percentageChange: 0,
    weekLabels: WEEK_LABELS,
};

const activitySkeletonRows = [0, 1, 2, 3, 4];
const formatChartCount = value => (Number.isFinite(Number(value)) ? Math.round(Number(value)) : 0);

const TimelineCard = () => {
  const reportNow = useReportClock();
    const [activityData, setActivityData] = useState(emptyActivityState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedAvisId, setSelectedAvisId] = useState('');

    useEffect(() => {
        let isMounted = true;

        const loadActivityData = async () => {


            try {
                const dashboardActivityData = await loadDashboardActivityData(new Date(), { skipCache: true });

                if (process.env.NODE_ENV !== 'production') {
                    console.info('[Dashboard Activite]', {
                        loadDurationMs: dashboardActivityData.loadDurationMs,
                        strategy: dashboardActivityData.firebaseQueryStrategy,
                        cacheHit: dashboardActivityData.cacheHit === true,
                    });
                }

                if (isMounted) {
                    setError('');
                    setActivityData({
                        ...emptyActivityState,
                        ...dashboardActivityData,
                    });
                }
            } catch (error) {
                if (isMounted) {
                    setError('Actualisation de l’activité indisponible. Les dernières données affichées peuvent être anciennes.');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadActivityData();

        return () => {
            isMounted = false;
        };
    }, [reportNow]);

    const trendSummary = useMemo(() => {
        const trendIsPositive = activityData.percentageChange >= 0;

        return {
            trendIsPositive,
            trendPercent: `${trendIsPositive ? '+' : ''}${activityData.percentageChange.toFixed(2)}%`,
        };
    }, [activityData.percentageChange]);

    const chartOptions = useMemo(() => ({
        ...AcivityChart.options,
        xaxis: {
            ...AcivityChart.options.xaxis,
            categories: activityData.weekLabels,
            labels: {
                ...AcivityChart.options.xaxis.labels,
                show: true,
            },
        },
        yaxis: {
            ...AcivityChart.options.yaxis,
            min: 0,
            forceNiceScale: true,
            labels: {
                ...AcivityChart.options.yaxis.labels,
                formatter: value => formatChartCount(value),
            },
        },
        tooltip: {
            ...AcivityChart.options.tooltip,
            y: {
                formatter: value => `${formatChartCount(value)} ${i18n.t('Dashboard_Timeline_Unit')}`,
            },
        },
    }), [activityData.weekLabels]);

    const chartSeries = useMemo(() => [{
        name: i18n.t('Dashboard_Timeline_Series'),
        data: activityData.currentWeekCounts,
    }], [activityData.currentWeekCounts]);

    const renderTicketNumber = ticketNumber => (
        ticketNumber
            ? (
                <span className="d-inline-flex align-items-center">
                    <span className="font-primary">#{ticketNumber}</span>
                    <AvisDetailModalTrigger avisId={ticketNumber} onOpen={setSelectedAvisId} />
                </span>
            )
            : <span className="font-primary">{i18n.t('Dashboard_Timeline_UnavailableNumber')}</span>
    );

    return (
        <>
        <Card>
            <div className="card-header">
                <div className="header-top d-sm-flex align-items-center">
                    <h5>{i18n.t('Dashboard_Timeline_Title')}</h5>
                    <div className="center-content">
                        <p className={trendSummary.trendIsPositive ? 'activity-trend-positive' : 'activity-trend-negative'}>
                            <i className={`${trendSummary.trendIsPositive ? 'toprightarrow-primary fa fa-arrow-up' : 'fa fa-arrow-down'} me-2`}></i>
                            {i18n.t('Dashboard_Timeline_PreviousSevenDays')} {trendSummary.trendPercent}
                        </p>
                    </div>
                </div>
            </div>
            <CardBody>
                {error && <div role="alert" className="alert alert-warning">{error}</div>}
                <div className="chart-main activity-timeline update-line">
                    {loading && (
                        <div className="activity-loading-state" aria-live="polite">
                            <div className="activity-empty-state activity-loading-message">
                                {i18n.t('Dashboard_Timeline_Loading')}
                            </div>
                            {activitySkeletonRows.map(row => (
                                <div className="activity-skeleton-row" key={row} aria-hidden="true">
                                    <span className="activity-skeleton-dot"></span>
                                    <div className="activity-skeleton-body">
                                        <span className="activity-skeleton-line activity-skeleton-date"></span>
                                        <span className="activity-skeleton-line activity-skeleton-title"></span>
                                        <span className="activity-skeleton-line activity-skeleton-client"></span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {!loading && !error && activityData.activities.length === 0 && (
                        <div className="activity-empty-state">
                            {i18n.t('Dashboard_Timeline_Empty')}
                        </div>
                    )}
                    {!loading && activityData.activities.map(activity => (
                        <div className="media" key={activity.id}>
                            <div className="activity-dot-primary"></div>
                            <div className="media-body d-block">
                                <h6 className="f-12 activity-meta">
                                    <span className="font-primary">{activity.date || '-'}</span>
                                    {activity.statusLabel && <span className="badge pill-badge-primary m-l-10">{activity.statusLabel}</span>}
                                    <span className="timer pull-right"><i className="fa fa-clock-o"></i> {activity.time || '--:--'}</span>
                                </h6>
                                <h5 className="f-12">{activity.type} {renderTicketNumber(activity.ticketNumber)}</h5>
                                {activity.clientName && <p>Client : {activity.clientName}</p>}
                            </div>
                        </div>
                    ))}
                    <div className="media activity-chart-media">
                        <div className="media-body d-block">
                            <div className="hospital-small-chart">
                                <div id="column-chart">
                                    <Chart options={chartOptions} series={chartSeries} type="bar" height={130} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
        <AvisDetailModal
            avisId={selectedAvisId}
            isOpen={Boolean(selectedAvisId)}
            toggle={() => setSelectedAvisId('')}
        />
        </>
    );
};

export default TimelineCard;
